import { useState } from 'react';
import { Hash, Copy, CheckCircle2, RefreshCw } from 'lucide-react';

export default function UUIDGenerator() {
  const [uuids, setUuids] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [copied, setCopied] = useState<number | null>(null);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const generate = () => {
    const newUUIDs = Array.from({ length: count }, () => generateUUID());
    setUuids(newUUIDs);
  };

  const copyToClipboard = (uuid: string, index: number) => {
    navigator.clipboard.writeText(uuid);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(uuids.join('\n'));
    setCopied(-1);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Hash className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">UUID Generator</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Generate v4 UUIDs</p>
        </div>
      </div>

      <div className="flex items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            Count
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent transition-all"
          />
        </div>
        <button
          onClick={generate}
          className="px-6 py-3 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Generate
        </button>
      </div>

      {uuids.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Generated UUIDs ({uuids.length})
            </label>
            <button
              onClick={copyAll}
              className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-200 dark:hover:bg-dark-border transition-colors flex items-center gap-1.5"
            >
              {copied === -1 ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  Copied All
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy All
                </>
              )}
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto bg-gray-50 dark:bg-dark-bg p-3 rounded-lg border border-gray-300 dark:border-gray-600">
            {uuids.map((uuid, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-white dark:bg-dark-surface rounded border border-gray-200 dark:border-dark-border hover:border-coral-400 dark:hover:border-coral-500 transition-colors"
              >
                <code className="text-sm font-mono text-gray-900 dark:text-dark-text">{uuid}</code>
                <button
                  onClick={() => copyToClipboard(uuid, index)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
                >
                  {copied === index ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {uuids.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
          <Hash className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No UUIDs Generated</p>
          <p className="text-sm">Click Generate to create new UUIDs</p>
        </div>
      )}
    </div>
  );
}
